import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Grid, TextField, Autocomplete} from 'views/components';
import {DateTimePicker} from 'views/module';
import {translate} from 'views/module';

import moment from 'moment';
import momentTZ from 'moment-timezone';

const FixedDate = (props) => {
  const newEvent = props.newEvent;
  const updateEventData = props.updateEventData;
  const setIsStepValid = props.setIsStepValid;

  const eventData = newEvent?.eventData || {};
  const startTime = eventData.startTime || null;
  const endTime = eventData.endTime || null;
  const breaks = newEvent.breaks || {breakDuration: 0, breakCount: 1};
  const timezone = eventData.timezone;

  const setStartTime = (newValue) => {
    updateEventData((e) => {
      return {
        ...e,
        startTime: newValue,
      };
    });
  };
  const setEndTime = (newValue) => {
    updateEventData((e) => {
      return {
        ...e,
        endTime: newValue,
      };
    });
  };

  const updateBreak = (field, value) => {
    if (value === '' || /^\d+$/.test(value)) {
      props.updateBreakDetails((e) => {
        return {
          ...e,
          [field]: parseInt(value) || 0,
        };
      });
    }
  };

  const setTimezone = (newValue) => {
    updateEventData((e) => {
      return {
        ...e,
        timezone: newValue,
      };
    });
  };

  // Sets default end time
  React.useEffect(() => {
    if (startTime && !endTime) {
      const startTimeMoment = moment(startTime);
      const endTimeMoment = startTimeMoment.add(2, 'hour');
      setEndTime(endTimeMoment);
    }
    if (endTime) {
      const startTimeMoment = moment(startTime);
      if (startTimeMoment.isAfter(moment(endTime))) {
        const endTimeMoment = startTimeMoment.add(2, 'hour');
        setEndTime(endTimeMoment);
      }
    }
  }, [startTime, endTime]);

  // Steps validation
  React.useEffect(() => {
    if (!(startTime && endTime && timezone)) {
      setIsStepValid(false);
      return;
    }
    if (endTime.isBefore(startTime)) {
      setIsStepValid(false);
      return;
    }

    // Validate breaks object
    if (breaks && breaks.breakDuration > 0 && breaks.breakCount > 0) {
      const duration = parseInt(breaks.breakDuration);
      const count = parseInt(breaks.breakCount);
      if (duration <= 0 || count <= 0 || isNaN(duration) || isNaN(count)) {
        setIsStepValid(false);
        return;
      }
      const eventDurationMinutes = moment(endTime).diff(moment(startTime), 'minutes');
      const totalBreakDuration = duration * count;
      if (totalBreakDuration >= eventDurationMinutes) {
        setIsStepValid(false);
        return;
      }
    }

    setIsStepValid(true);
  }, [startTime, endTime, timezone, breaks]);

  // Sets default timezone
  React.useEffect(() => {
    if (!timezone) {
      setTimezone(momentTZ.tz.guess());
    }
  }, [timezone]);

  // Sets default breaks
  React.useEffect(() => {
    if (!newEvent.breaks) {
      props.updateBreakDetails(() => {
        return {
          breakDuration: 0,
          breakCount: 1,
        };
      });
    }
  }, []);

  const makeTimezonesList = React.useCallback(() => {
    const timezones = momentTZ.tz.names();
    const formatted = timezones.map((timezone) => {
      return {
        group: translate('client.ClientDetail.Scheduling.Timezone.Timezones'),
        value: timezone,
        label: timezone,
      };
    });
    return [...formatted];
  }, []);

  return (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <DateTimePicker
          label={translate('client.ClientDetail.Scheduling.ScheduleDetail.EventDate.FixedEvent.StartTime')}
          value={startTime}
          onChange={(e) => setStartTime(e)}
        />
      </Grid>
      <Grid item xs={6}>
        <DateTimePicker
          label={translate('client.ClientDetail.Scheduling.ScheduleDetail.EventDate.FixedEvent.EndTime')}
          value={endTime}
          onChange={setEndTime}
        />
      </Grid>
      <Grid item xs={12}>
        <Autocomplete
          options={makeTimezonesList()}
          value={timezone || momentTZ.tz.guess()}
          label={translate('client.ClientDetail.Scheduling.ScheduleDetail.EventDate.FixedEvent.Timezone')}
          onChange={(e) => setTimezone(e.value)}
          sx={{minWidth: {xs: '192px', md: '256px'}}}
          renderOption={(props, option) => {
            return (
              <li {...props} key={option.value}>
                {option.label}
              </li>
            );
          }}
          groupBy={(dropdownItem) => dropdownItem.group}
          isOptionEqualToValue={(option, value) => option.value === value}
          disableClearable
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          fullWidth
          type="number"
          label={translate('client.ClientDetail.Scheduling.ScheduleDetail.Breaks.BreakDuration')}
          value={breaks.breakDuration}
          onChange={(e) => {
            updateBreak('breakDuration', e);
          }}
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          fullWidth
          type="number"
          label={translate('client.ClientDetail.Scheduling.ScheduleDetail.Breaks.BreakCount')}
          value={breaks.breakCount}
          onChange={(e) => {
            updateBreak('breakCount', e);
          }}
        />
      </Grid>
    </Grid>
  );
};

FixedDate.propTypes = {
  eventType: PropTypes.oneOf(['RECURRENCE_EVENT', 'FIXED_EVENT', 'LIVEIN_EVENT']),
  setEventType: PropTypes.func.isRequired,
  setIsStepValid: PropTypes.func.isRequired,
  handleNext: PropTypes.func.isRequired,
  newEvent: PropTypes.object.isRequired,
  updateEventData: PropTypes.func.isRequired,
  updateRecurrenceData: PropTypes.func.isRequired,
  updateLiveInData: PropTypes.func.isRequired,
  updateBreakDetails: PropTypes.func.isRequired,
};
const mapStateToProps = (state) => ({
});
export default connect(mapStateToProps, dispatch)(React.memo(FixedDate));
